import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const DashboardHeader = () => {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const profileDropdownRef = useRef(null);
    const [showProfileDropdown, setShowProfileDropdown] = useState(false);

    // Generate a deterministic color based on email
    const getColorFromEmail = useCallback((email) => {
        if (!email) return '#6366f1'; // default primary color

        // Simple hash function
        let hash = 0;
        for (let i = 0; i < email.length; i++) {
            hash = email.charCodeAt(i) + ((hash << 5) - hash);
        }

        // Generate vibrant colors
        const colors = [
            '#8b5cf6', // violet
            '#ec4899', // pink
            '#f59e0b', // amber
            '#10b981', // emerald
            '#06b6d4', // cyan
            '#ef4444', // red
            '#14b8a6', // teal
            '#f97316', // orange
        ];

        const index = Math.abs(hash) % colors.length;
        return colors[index];
    }, []);

    const getUserInitial = useCallback(() => {
        if (user?.displayName) {
            return user.displayName.charAt(0).toUpperCase();
        }
        return user?.email?.charAt(0).toUpperCase() || 'U';
    }, [user]);

    const avatarColor = useMemo(() => getColorFromEmail(user?.email), [user?.email, getColorFromEmail]);

    // Handle click outside profile dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target)) {
                setShowProfileDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleLogout = async () => {
        try {
            await logout();
            navigate('/');
        } catch (err) {
            console.error('Logout error:', err);
        }
    };

    return (
        <header className="glass-panel border-0 border-bottom sticky-top py-3 px-3 px-md-4 mb-4 mb-md-5" style={{ borderRadius: 0 }}>
            <div className="container d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center gap-2 gap-md-3 cursor-pointer" onClick={() => navigate('/dashboard')}>
                    <img
                        src="/logo192.png"
                        alt="FormX Logo"
                        className="rounded-3 shadow-sm"
                        style={{ width: '40px', height: '40px', objectFit: 'cover' }}
                    />
                    <h1 className="h4 mb-0 fw-bold" style={{ fontFamily: 'Outfit, sans-serif', letterSpacing: '-0.5px' }}>FormX</h1>
                </div>

                <div className="profile-dropdown-container" ref={profileDropdownRef}>
                    <button
                        onClick={() => setShowProfileDropdown(!showProfileDropdown)}
                        className="profile-button"
                        title="Profile Settings"
                        style={{ backgroundColor: avatarColor, color: 'white' }}
                    >
                        <span className="fw-bold">{getUserInitial()}</span>
                    </button>

                    {showProfileDropdown && (
                        <div className="profile-dropdown-menu">
                            <div className="profile-header">
                                <div className="profile-avatar-large" style={{ backgroundColor: avatarColor }}>
                                    <span>{getUserInitial()}</span>
                                </div>
                                <div className="overflow-hidden">
                                    <h6 className="mb-0 fw-bold text-truncate">{user?.displayName || 'User'}</h6>
                                    <p className="mb-0 text-muted small text-truncate">{user?.email}</p>
                                </div>
                            </div>

                            <div className="profile-info-item">
                                <span className="profile-info-label">Member Since</span>
                                <span className="profile-info-value">
                                    {user?.metadata?.creationTime
                                        ? new Date(user.metadata.creationTime).toLocaleDateString(undefined, { month: 'long', year: 'numeric' })
                                        : 'N/A'}
                                </span>
                            </div>

                            <button
                                onClick={handleLogout}
                                className="logout-btn-premium"
                            >
                                <i className="fa fa-sign-out me-2" aria-hidden="true"></i>
                                Logout
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </header>
    );
};

export default DashboardHeader;
